import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

type DiagnosticsOnboardDialogProps = {
  open: boolean;
  deviceId: string | null;
  loading: boolean;
  result: any | null;
  edgeHost: string;
  cloudHost: string;
  cloudApiUrl: string;
  cloudMqttPort: string;
  edgePassword: string;
  onClose: () => void;
  onRun: () => void;
  onEdgeHostChange: (value: string) => void;
  onCloudHostChange: (value: string) => void;
  onCloudApiUrlChange: (value: string) => void;
  onCloudMqttPortChange: (value: string) => void;
  onEdgePasswordChange: (value: string) => void;
};

export default function DiagnosticsOnboardDialog({
  open,
  deviceId,
  loading,
  result,
  edgeHost,
  cloudHost,
  cloudApiUrl,
  cloudMqttPort,
  edgePassword,
  onClose,
  onRun,
  onEdgeHostChange,
  onCloudHostChange,
  onCloudApiUrlChange,
  onCloudMqttPortChange,
  onEdgePasswordChange,
}: DiagnosticsOnboardDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Onboard Edge Device (mTLS)</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This runs the operator onboarding script via the deploy runner: it issues split API+MQTT per-device client
          certs, copies them to the edge, writes `/etc/edgentik/edgentik.env` with split mTLS paths, updates the native
          orchestrator override, and deploys the edge-services container.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Edge Host/IP (current)"
              value={edgeHost}
              onChange={(e) => onEdgeHostChange(e.target.value)}
              placeholder="e.g. edge-host or IP"
              helperText="Do not bind device identity to a static IP. Provide the edge's current reachable host/IP for onboarding."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cloud Host/IP (edge-facing)"
              value={cloudHost}
              onChange={(e) => onCloudHostChange(e.target.value)}
              placeholder="e.g. cloud-host or IP"
              helperText="Used to derive https://<cloud-host>:8443 and MQTT TLS reachability. Leave blank to use the cloud hostname."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cloud API URL (optional override)"
              value={cloudApiUrl}
              onChange={(e) => onCloudApiUrlChange(e.target.value)}
              placeholder="e.g. https://cloud-host:8443"
              helperText="Leave blank to use https://<cloud-host>:8443."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cloud MQTT TLS Port"
              value={cloudMqttPort}
              onChange={(e) => onCloudMqttPortChange(e.target.value)}
              placeholder="8883"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Edge SSH password (optional, first-boot only)"
              value={edgePassword}
              onChange={(e) => onEdgePasswordChange(e.target.value)}
              placeholder="leave blank if key-based SSH already works"
              helperText="Used only to bootstrap SSH key access on brand new devices. Not required for already-onboarded units."
            />
          </Grid>
        </Grid>

        {result && (
          <Alert severity={result.ok ? 'success' : 'warning'} sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Onboard result</Typography>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(result, null, 2)}</pre>
            <Typography variant="caption" color="text.secondary">
              Tip: use "Edge Update Logs" to watch progress if the job is still running.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button variant="contained" onClick={onRun} disabled={!deviceId || loading}>
          {loading ? 'Onboarding...' : 'Run Onboard'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
